import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, CssBaseline } from '@mui/material';
import { AuthProvider } from './context/AuthContext';
import { SettingsProvider } from './context/SettingsContext';
import { saasTheme } from './theme/saasTheme';
import RequireAuth from './components/RequireAuth';
import AppShell from './components/layout/AppShell';
import OverviewPage from './pages/OverviewPage';
import ApiSettings from './pages/ApiSettings';
import PlaceholderPage from './pages/PlaceholderPage';
import IntegrationLandingPage from './pages/IntegrationLandingPage';
import DudaDashboard from './pages/DudaDashboard';
import DudaSiteAnalytics from './pages/DudaSiteAnalytics';
import DudaAnalyticsCachePage from './pages/DudaAnalyticsCachePage';
import StripeLayout from './pages/stripe/StripeLayout';
import StripeOverviewPage from './pages/stripe/StripeOverviewPage';
import StripeTransactionsPage from './pages/stripe/StripeTransactionsPage';
import StripeCustomersPage from './pages/stripe/StripeCustomersPage';
import StripeSubscriptionsPage from './pages/stripe/StripeSubscriptionsPage';
import StripePayoutsPage from './pages/stripe/StripePayoutsPage';
import BillingPage from './pages/BillingPage';
import IncomeReportPage from './pages/IncomeReportPage';
import UsersSettingsPage from './pages/UsersSettingsPage';
import XeroLayout from './pages/xero/XeroLayout';
import XeroReportsPage from './pages/xero/XeroReportsPage';
import XeroInvoicesPage from './pages/xero/XeroInvoicesPage';
import XeroCustomersPage from './pages/xero/XeroCustomersPage';
import MetaDashboard from './pages/MetaDashboard';
import AuthCallback from './components/AuthCallback';
import Login from './components/Login';

function App() {
  return (
    <ThemeProvider theme={saasTheme}>
      <CssBaseline />
      <AuthProvider>
        <SettingsProvider>
          <Router>
            <Routes>
              <Route path="/login" element={<Login />} />
              <Route path="/auth/callback" element={<AuthCallback />} />
              <Route element={<RequireAuth />}>
                <Route element={<AppShell />}>
                  <Route index element={<OverviewPage />} />
                  <Route path="sales" element={<Navigate to="/" replace />} />
                  <Route path="duda/analytics-cache" element={<DudaAnalyticsCachePage />} />
                  <Route path="duda" element={<DudaDashboard />} />
                  <Route path="duda/site/:siteName" element={<DudaSiteAnalytics />} />
                  <Route path="stripe" element={<StripeLayout />}>
                    <Route index element={<Navigate to="overview" replace />} />
                    <Route path="overview" element={<StripeOverviewPage />} />
                    <Route path="transactions" element={<StripeTransactionsPage />} />
                    <Route path="customers" element={<StripeCustomersPage />} />
                    <Route path="subscriptions" element={<StripeSubscriptionsPage />} />
                    <Route path="payouts" element={<StripePayoutsPage />} />
                  </Route>
                  <Route path="xero" element={<XeroLayout />}>
                    <Route index element={<Navigate to="reports" replace />} />
                    <Route path="reports" element={<XeroReportsPage />} />
                    <Route path="invoices" element={<XeroInvoicesPage />} />
                    <Route path="customers" element={<XeroCustomersPage />} />
                  </Route>
                  <Route path="billing" element={<BillingPage />} />
                  <Route path="reports/income" element={<IncomeReportPage />} />
                  <Route path="meta" element={<MetaDashboard />} />
                  <Route path="integrations/xero" element={<Navigate to="/xero" replace />} />
                  <Route path="integrations/:slug" element={<IntegrationLandingPage />} />
                  <Route
                    path="sync"
                    element={
                      <PlaceholderPage
                        title="Sync data"
                        subtitle="Pull the latest from Stripe, Xero, Duda, and Google Ads on a schedule. Coming soon — you will trigger syncs and see job status per workspace."
                      />
                    }
                  />
                  <Route
                    path="audit"
                    element={
                      <PlaceholderPage
                        title="Audit"
                        subtitle="Immutable log of who changed API keys, connected accounts, and workspace settings. Designed for multi-tenant SaaS compliance."
                      />
                    }
                  />
                  <Route path="settings/users" element={<UsersSettingsPage />} />
                  <Route path="settings" element={<ApiSettings />} />
                </Route>
              </Route>
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </Router>
        </SettingsProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
